<template>
  <div
    class="rich-editor"
    :class="{
      'handout-mode': handoutMode,
      'format-painter-active': formatPainterActive,
      'preview-only': previewOnly
    }"
    :style="rootStyle"
  >
    <div v-if="!previewOnly" class="editor-toolbar-wrap">
      <button
        v-if="handoutMode"
        type="button"
        class="format-painter-btn"
        :class="{ active: formatPainterActive, sticky: formatPainterSticky }"
        title="格式刷（再次点击或按 Esc 取消，双击连续刷）"
        @mousedown.prevent="onFormatPainterMouseDown"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" class="format-painter-icon">
          <path d="M7 3h10a2 2 0 0 1 2 2v3.5l-7.5 7.5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" fill="currentColor" opacity="0.18" />
          <path d="M9 5h6v2.8L10.2 12.6 9 11.4V5zm8.8 4.2 5.2 5.2a1.2 1.2 0 0 1 0 1.7l-1.6 1.6a1.2 1.2 0 0 1-1.7 0l-5.2-5.2 3.3-3.3z" fill="currentColor" />
        </svg>
      </button>
      <Toolbar
        :editor="editorRef"
        :defaultConfig="toolbarConfig"
        :mode="mode"
        class="editor-toolbar"
      />
    </div>
    <div class="editor-body">
      <div ref="editorScroll" class="editor-scroll">
        <div v-if="handoutMode" class="handout-title-banner">{{ handoutTitle || '未命名讲义' }}</div>
        <Editor
          :defaultConfig="mergedEditorConfig"
          :mode="mode"
          :value="html"
          @onCreated="handleCreated"
          @onChange="handleChange"
          :style="editorSurfaceStyle"
        />
        <div
          v-if="handoutMode && !previewOnly && handoutQuestionHighlight.visible"
          class="handout-question-block-highlight"
          :style="handoutQuestionHighlightStyle"
        ></div>
        <div
          v-if="handoutMode && !previewOnly && handoutQuestionToolbar.visible"
          ref="handoutQuestionToolbar"
          class="handout-question-toolbar"
          :style="handoutQuestionToolbarStyle"
          @mousedown.prevent
        >
          <span class="handout-question-toolbar-title">题目设置</span>
          <span class="handout-question-toolbar-divider"></span>
          <span class="handout-question-toolbar-label">选项排列</span>
          <button
            v-for="item in optionLayoutChoices"
            :key="item.value"
            type="button"
            class="handout-option-layout-btn"
            :class="{ active: handoutQuestionToolbar.layout === item.value }"
            :title="item.label"
            @click="applyHandoutQuestionOptionLayout(item.value)"
          >
            <span class="layout-icon" :data-cols="item.value">{{ item.icon }}</span>
            <span class="layout-text">{{ item.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import '@wangeditor/editor/dist/css/style.css'
import '../styles/handout-headings.css'
import '../styles/handout-preview-pages.css'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { DomEditor, SlateTransforms, SlateEditor, SlateRange, SlateElement } from '@wangeditor/editor'
import { cleanEditorHtml, resolveCosImageUrl, fixCosImageUrlsInHtml } from '../utils/renderHtml'
import {
  applyHandoutPageBackground,
  calcHandoutPageHeight,
  computeHandoutPaginationPlan,
  embedHandoutPaginationComment,
  extractHandoutPaginationPlan,
  mergeIncomingHandoutPlan,
  stripHandoutPaginationComment,
  buildHandoutPreviewPageNumberPositions
} from '../utils/handoutPagination'
import {
  buildQuestionMetaInnerHtml,
  cleanupStrayQuestionIndexNodes,
  extractQuestionMetaPrefix,
  isQuestionMetaText,
  QUESTION_TYPE_RE,
  splitQuestionMetaLine,
  stripHandoutQuestionKnowledgeSpans,
  stripLeadingQuestionIndex
} from '../utils/handoutDecorate'
import { ensureQuestionBlockIds, getQuestionBlockRangesFromRoot, isWithinQuestionCoreBlock, moveQuestionBlockByIndexInHtml, renumberInlineQuestionIndices, renumberQuestionsInHtml, resolveQuestionBlockMetaFromTarget, collectQuestionBlockNodes, collectQuestionHighlightNodes } from '../utils/handoutQuestionBlocks'
import {
  OPTION_LAYOUT_CHOICES,
  applyOptionLayoutToBlockInRoot,
  applyOptionLayoutMapToRoot,
  applyOptionLayoutsFromPlan,
  blockHasOptions,
  collectOptionLayoutMapFromRoot,
  ensureOptionLayoutsInHtml,
  ensureOptionLayoutsInRoot,
  getBlockOptionLayout,
  inferLayoutFromRenderedOptions,
  mergeLayoutMapsPreferPlan,
  mergeOptionLayoutsIntoPlan,
  normalizeOptionLayout,
  pickPreferredLayoutValue,
  resolveBlockOptionLayout,
  upsertOptionLayoutInPlan
} from '../utils/handoutOptionLayout'

const FORMAT_MARK_KEYS = ['bold', 'italic', 'underline', 'through', 'color', 'bgColor', 'fontSize', 'fontFamily', 'sup', 'sub']
const FORMAT_BLOCK_TYPES = ['paragraph', 'header1', 'header2', 'header3', 'header4', 'header5']

/** wangEditor headerSelect 不读取 MENU_CONF.options；下拉仅显示 H1/H2… 文字 */
const HANDOUT_HEADER_SELECT_OPTIONS = [
  { value: 'header1', text: 'H1' },
  { value: 'header2', text: 'H2' },
  { value: 'header3', text: 'H3' },
  { value: 'header4', text: 'H4' },
  { value: 'header5', text: 'H5' },
  { value: 'paragraph', text: '正文' }
]

export default {
  name: 'RichEditor',
  components: { Editor, Toolbar },
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: '请输入内容...' },
    height: { type: [String, Number], default: 150 },
    uploadServer: { type: String, default: '/api/admin/upload/image' },
    extended: { type: Boolean, default: false },
    handoutMode: { type: Boolean, default: false },
    handoutTitle: { type: String, default: '' },
    showQuestionSource: { type: Boolean, default: true },
    showQuestionDifficulty: { type: Boolean, default: true },
    previewOnly: { type: Boolean, default: false },
    showPageNumbers: { type: Boolean, default: false },
    preferredContentWidth: { type: Number, default: 0 }
  },
  data() {
    const uploadConf = {
      server: this.uploadServer,
      fieldName: 'file',
      maxFileSize: 2 * 1024 * 1024,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('admin_token')}`
      },
      customInsert(res, insertFn) {
        const payload = res && res.data != null && typeof res.data === 'object' && !Array.isArray(res.data)
          ? res.data
          : res
        let url = (payload && payload.url) || (res && res.url) || ''
        if (!url) {
          throw new Error((res && res.message) || '图片上传失败：未返回图片地址')
        }
        url = resolveCosImageUrl(url)
        insertFn(url, '', url)
      }
    }
    return {
      editorRef: null,
      mode: 'default',
      html: this.value || '',
      _suppressChange: false,
      _selectedPageBreakNode: null,
      formatPainterCaptured: null,
      formatPainterActive: false,
      formatPainterSticky: false,
      _formatPainterClickTimer: null,
      _formatPainterApplyTimer: null,
      _formatPainterSourceKey: '',
      _handoutFullLayoutTimer: null,
      _modelSyncTimer: null,
      _handoutDecorating: false,
      _headerSelectPatched: false,
      _headerSelectScrollBound: false,
      _handoutQuestionClickBound: false,
      _handoutQuestionScrollBound: false,
      _handoutPaginationPlan: null,
      _handoutComposing: false,
      handoutQuestionToolbar: {
        visible: false,
        blockId: '',
        layout: 1,
        top: 0,
        left: 0
      },
      handoutQuestionHighlight: {
        visible: false,
        top: 0,
        left: 0,
        width: 0,
        height: 0
      },
      optionLayoutChoices: OPTION_LAYOUT_CHOICES,
      previewEditorHeight: 480,
      baseEditorConfig: {
        placeholder: this.placeholder,
        MENU_CONF: {
          uploadImage: uploadConf,
          fontFamily: {
            fontFamilyList: [
              '微软雅黑',
              '宋体',
              '黑体',
              '楷体',
              '仿宋',
              'Arial',
              'Times New Roman'
            ]
          },
          fontSize: {
            fontSizeList: [
              { name: '初号', value: '42px' },
              { name: '小初', value: '36px' },
              { name: '一号', value: '26px' },
              { name: '小一', value: '24px' },
              { name: '二号', value: '22px' },
              { name: '小二', value: '18px' },
              { name: '三号', value: '16px' },
              { name: '小三', value: '15px' },
              { name: '四号', value: '14px' },
              { name: '小四', value: '12px' },
              { name: '五号', value: '10.5px' }
            ]
          },
          lineHeight: {
            lineHeightList: ['1', '1.15', '1.5', '1.75', '2', '2.5', '3']
          }
        },
        hoverbarKeys: {
          table: {
            menuKeys: [
              'enter', 'tableHeader', 'tableFullWidth',
              'insertTableRow', 'deleteTableRow', 'insertTableCol', 'deleteTableCol', 'deleteTable'
            ]
          },
          image: {
            menuKeys: ['imageWidth30', 'imageWidth50', 'imageWidth100', '|', 'justifyLeft', 'justifyCenter', 'justifyRight', 'editImage']
          }
        }
      }
    }
  },
  computed: {
    mergedEditorConfig() {
      return {
        ...this.baseEditorConfig,
        placeholder: this.placeholder,
        readOnly: this.previewOnly
      }
    },
    rootStyle() {
      const width = Number(this.preferredContentWidth) || 0
      if (this.previewOnly && width > 0) {
        return { width: `${Math.max(640, width)}px`, maxWidth: '100%' }
      }
      return {}
    },
    editorSurfaceStyle() {
      if (this.previewOnly) {
        return {
          height: `${Math.max(200, this.previewEditorHeight)}px`,
          overflowY: 'visible'
        }
      }
      return {
        height: typeof this.height === 'number' ? `${this.height}px` : this.height,
        overflowY: 'auto'
      }
    },
    handoutQuestionToolbarStyle() {
      const toolbar = this.handoutQuestionToolbar || {}
      return {
        top: `${toolbar.top || 0}px`,
        left: `${toolbar.left || 0}px`
      }
    },
    handoutQuestionHighlightStyle() {
      const box = this.handoutQuestionHighlight || {}
      return {
        top: `${box.top || 0}px`,
        left: `${box.left || 0}px`,
        width: `${box.width || 0}px`,
        height: `${box.height || 0}px`
      }
    },
    toolbarConfig() {
      if (this.handoutMode || this.extended) {
        return {
          toolbarKeys: [
            'undo', 'redo', '|',
            'headerSelect', 'fontFamily', 'fontSize', '|',
            'insertTable', '|',
            'bulletedList', 'numberedList', '|',
            'justifyLeft', 'justifyCenter', 'justifyRight', '|',
            'bold', 'italic', 'underline', 'through', '|',
            'sup', 'sub', '|',
            'color', 'bgColor', '|',
            'uploadImage'
          ]
        }
      }
      return {
        toolbarKeys: [
          'undo', 'redo', '|',
          'insertTable', '|',
          'bulletedList', 'numberedList', '|',
          'bold', 'italic', 'underline', 'through', '|',
          'sup', 'sub', '|',
          'color', 'bgColor', '|',
          'uploadImage'
        ]
      }
    }
  },
  watch: {
    value(val) {
      const next = val || ''
      if (next === this.html) return
      if (this.handoutMode && this.isHandoutEditingFrozen()) return
      this.html = next
      this.syncEditorHtml(next)
    },
    showQuestionSource() {
      if (this.handoutMode && this.isEditorFocused()) {
        const root = this.getEditableRoot()
        if (root) {
          root.classList.toggle('hide-question-source', !this.showQuestionSource)
          this.applyQuestionMetaVisibility(root, true)
        }
        return
      }
      this.decorateHandoutEditor()
    },
    showQuestionDifficulty() {
      if (this.handoutMode && this.isEditorFocused()) {
        const root = this.getEditableRoot()
        if (root) {
          root.classList.toggle('hide-question-difficulty', !this.showQuestionDifficulty)
          this.applyQuestionMetaVisibility(root, true)
        }
        return
      }
      this.decorateHandoutEditor()
    },
    handoutTitle() {
      this.scheduleHandoutLayout()
    },
    showPageNumbers() {
      if (this.previewOnly) this.applyHandoutEditorLayout()
    }
  },
  beforeDestroy() {
    this.unbindHandoutBannerResizeSync()
    this.unbindHandoutQuestionScrollSync()
    if (this._formatPainterClickTimer) {
      clearTimeout(this._formatPainterClickTimer)
      this._formatPainterClickTimer = null
    }
    this.teardownFormatPainterListener()
    if (this._formatPainterApplyTimer) {
      clearTimeout(this._formatPainterApplyTimer)
      this._formatPainterApplyTimer = null
    }
    if (this._handoutFullLayoutTimer) {
      clearTimeout(this._handoutFullLayoutTimer)
      this._handoutFullLayoutTimer = null
    }
    if (this._modelSyncTimer) {
      clearTimeout(this._modelSyncTimer)
      this._modelSyncTimer = null
    }
    this.flushModelSync()
    const editor = this.editorRef
    if (editor && typeof editor.getEditableContainer === 'function') {
      const editable = editor.getEditableContainer()
      if (editable) {
        editable.removeEventListener('keydown', this.handleHandoutKeydown, true)
        editable.removeEventListener('mousedown', this.handleHandoutMouseDown, true)
        editable.removeEventListener('mouseup', this.handleHandoutMouseUp, true)
        editable.removeEventListener('blur', this.handleHandoutBlur, true)
        editable.removeEventListener('click', this.handleHandoutQuestionClick, true)
        editable.removeEventListener('compositionstart', this.handleHandoutCompositionStart, true)
        editable.removeEventListener('compositionend', this.handleHandoutCompositionEnd, true)
      }
    }
    if (editor) {
      editor.destroy()
    }
  },
  methods: {
    handleCreated(editor) {
      this.editorRef = editor
      const editable = editor.getEditableContainer && editor.getEditableContainer()
      if (editable) {
        editable.setAttribute('spellcheck', 'false')
        if (!this.previewOnly) {
          editable.addEventListener('keydown', this.handleHandoutKeydown, true)
          editable.addEventListener('mousedown', this.handleHandoutMouseDown, true)
          editable.addEventListener('mouseup', this.handleHandoutMouseUp, true)
          editable.addEventListener('blur', this.handleHandoutBlur, true)
          editable.addEventListener('click', this.handleHandoutQuestionClick, true)
          editable.addEventListener('compositionstart', this.handleHandoutCompositionStart, true)
          editable.addEventListener('compositionend', this.handleHandoutCompositionEnd, true)
        }
      }
      this.syncEditorHtml(this.value || '')
      this.$nextTick(() => {
        this.scheduleHandoutHeaderSelectPatch()
        this.bindHandoutQuestionToolbarEvents()
        this.bindHandoutQuestionScrollSync()
        this.decorateHandoutEditor()
        if (this.handoutMode) {
          setTimeout(() => this.flushHandoutEditorLayout(), 120)
        }
      })
    },
    scheduleHandoutHeaderSelectPatch() {
      if (!this.handoutMode) return
      ;[0, 80, 200, 500, 1000].forEach((delay) => {
        setTimeout(() => this.patchHandoutHeaderSelectMenu(), delay)
      })
    },
    patchHandoutHeaderSelectMenu() {
      if (!this.handoutMode || !this.editorRef || this._headerSelectPatched) return
      const toolbar = DomEditor.getToolbar(this.editorRef)
      if (!toolbar || typeof toolbar.getMenus !== 'function') return
      const menu = toolbar.getMenus().headerSelect
      if (!menu || typeof menu.getValue !== 'function') return

      const getValue = menu.getValue.bind(menu)
      menu.getOptions = (editor) => {
        const current = String(getValue(editor))
        return HANDOUT_HEADER_SELECT_OPTIONS.map((option) => {
          const item = { value: option.value, text: option.text }
          if (item.value === current) item.selected = true
          return item
        })
      }
      this._headerSelectPatched = true
      this.bindHeaderSelectDropdownScrollReset()
      if (typeof toolbar.changeToolbarState === 'function') {
        toolbar.changeToolbarState()
      }
    },
    bindHeaderSelectDropdownScrollReset() {
      if (this._headerSelectScrollBound || !this.$el) return
      const btn = this.$el.querySelector('[data-menu-key="headerSelect"]')
      if (!btn) return
      btn.addEventListener('click', () => {
        this.$nextTick(() => {
          const list = this.$el.querySelector('.w-e-select-list')
          if (list) list.scrollTop = 0
        })
      })
      this._headerSelectScrollBound = true
    },
    handleHandoutBlur() {
      if (!this.handoutMode || this.previewOnly) return
      this.hideHandoutQuestionToolbar()
      this.decorateHandoutEditor(true)
      this.flushModelSync(true)
      this.$emit('editor-blur')
    },
    handleHandoutCompositionStart() {
      this._handoutComposing = true
    },
    handleHandoutCompositionEnd() {
      this._handoutComposing = false
    },
    bindHandoutQuestionToolbarEvents() {
      if (!this.handoutMode || this.previewOnly || this._handoutQuestionClickBound) return
      const editable = this.editorRef && this.editorRef.getEditableContainer && this.editorRef.getEditableContainer()
      if (!editable) return
      editable.addEventListener('click', this.handleHandoutQuestionClick, true)
      this._handoutQuestionClickBound = true
    },
    bindHandoutQuestionScrollSync() {
      if (!this.handoutMode || this.previewOnly || this._handoutQuestionScrollBound) return
      const scrollEl = this.getEditorScrollContainer()
      if (!scrollEl) return
      scrollEl.addEventListener('scroll', this.handleHandoutQuestionScroll, { passive: true })
      this._handoutQuestionScrollBound = true
    },
    unbindHandoutQuestionScrollSync() {
      if (!this._handoutQuestionScrollBound) return
      const scrollEl = this.getEditorScrollContainer()
      if (scrollEl) scrollEl.removeEventListener('scroll', this.handleHandoutQuestionScroll)
      this._handoutQuestionScrollBound = false
    },
    handleHandoutQuestionScroll() {
      if (!this.handoutQuestionToolbar.visible) return
      this.refreshHandoutQuestionSelectionVisual()
    },
    resolveQuestionOptionLayout(meta, blockId) {
      if (!meta || !blockId) return 1
      const questionId = meta.getAttribute('data-question-id') || ''
      const layoutMap = this._handoutPaginationPlan && this._handoutPaginationPlan.optionLayouts
      return resolveBlockOptionLayout(blockId, questionId, meta, layoutMap)
    },
    handleHandoutQuestionClick(event) {
      if (!this.handoutMode || this.previewOnly) return
      if (event.target && event.target.closest && event.target.closest('.handout-question-toolbar')) return
      const root = this.getEditableRoot()
      if (!root) return
      const meta = resolveQuestionBlockMetaFromTarget(root, event.target)
      if (!meta) {
        this.hideHandoutQuestionToolbar()
        return
      }
      const blockId = meta.getAttribute('data-handout-question-block')
      if (!blockId || !blockHasOptions(meta, blockId)) {
        this.hideHandoutQuestionToolbar()
        return
      }
      if (!isWithinQuestionCoreBlock(meta, blockId, event.target)) {
        this.hideHandoutQuestionToolbar()
        return
      }
      this.handoutQuestionToolbar = {
        visible: true,
        blockId,
        layout: this.resolveQuestionOptionLayout(meta, blockId),
        top: this.handoutQuestionToolbar.top || 0,
        left: this.handoutQuestionToolbar.left || 0
      }
      this.$nextTick(() => {
        this.updateHandoutQuestionBlockHighlight(meta, blockId)
        this.updateHandoutQuestionToolbarPosition(meta)
        this.$nextTick(() => this.updateHandoutQuestionToolbarPosition(meta))
      })
    },
    getQuestionBlockScreenBounds(metaNode, blockId) {
      const root = this.getEditableRoot()
      const nodes = collectQuestionHighlightNodes(metaNode, blockId, root)
      if (!nodes.length) return null
      const metaRect = metaNode.getBoundingClientRect()
      let top = metaRect.top
      let left = metaRect.left
      let bottom = metaRect.bottom
      let right = metaRect.right
      nodes.forEach(node => {
        const rect = node.getBoundingClientRect()
        if (!rect.width && !rect.height) return
        left = Math.min(left, rect.left)
        right = Math.max(right, rect.right)
        bottom = Math.max(bottom, rect.bottom)
      })
      if (!Number.isFinite(top) || !Number.isFinite(bottom)) return null
      return { top, left, bottom, right }
    },
    updateHandoutQuestionBlockHighlight(metaNode, blockId) {
      const scrollEl = this.$refs.editorScroll
      if (!scrollEl || !metaNode || !blockId) return
      const bounds = this.getQuestionBlockScreenBounds(metaNode, blockId)
      if (!bounds) return
      const scrollRect = scrollEl.getBoundingClientRect()
      const padding = 6
      this.handoutQuestionHighlight = {
        visible: true,
        top: bounds.top - scrollRect.top + scrollEl.scrollTop - padding,
        left: bounds.left - scrollRect.left + scrollEl.scrollLeft - padding,
        width: Math.max(0, bounds.right - bounds.left + padding * 2),
        height: Math.max(0, bounds.bottom - bounds.top + padding * 2)
      }
    },
    refreshHandoutQuestionSelectionVisual() {
      const meta = this.getSelectedQuestionMetaNode()
      const blockId = this.handoutQuestionToolbar.blockId
      if (!this.handoutQuestionToolbar.visible || !meta || !blockId) return
      this.updateHandoutQuestionBlockHighlight(meta, blockId)
      this.updateHandoutQuestionToolbarPosition(meta)
    },
    updateHandoutQuestionToolbarPosition(metaNode) {
      const scrollEl = this.$refs.editorScroll
      const meta = metaNode || this.getSelectedQuestionMetaNode()
      const blockId = this.handoutQuestionToolbar.blockId
      if (!scrollEl || !meta || !blockId) return
      const bounds = this.getQuestionBlockScreenBounds(meta, blockId)
      if (!bounds) return
      const scrollRect = scrollEl.getBoundingClientRect()
      const toolbarEl = this.$refs.handoutQuestionToolbar
      const toolbarWidth = (toolbarEl && toolbarEl.offsetWidth) || 320
      const toolbarHeight = (toolbarEl && toolbarEl.offsetHeight) || 44
      const gap = 8
      const blockRight = bounds.right - scrollRect.left + scrollEl.scrollLeft
      const blockTop = bounds.top - scrollRect.top + scrollEl.scrollTop
      let posLeft = blockRight - toolbarWidth - gap
      let posTop = blockTop - toolbarHeight - gap
      if (posTop < 8) posTop = blockTop + gap
      posLeft = Math.max(8, Math.min(posLeft, scrollEl.clientWidth - toolbarWidth - 8))
      this.handoutQuestionToolbar = {
        ...this.handoutQuestionToolbar,
        top: posTop,
        left: posLeft
      }
    },
    getSelectedQuestionMetaNode() {
      const root = this.getEditableRoot()
      const blockId = this.handoutQuestionToolbar.blockId
      if (!root || !blockId) return null
      return root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
    },
    hideHandoutQuestionToolbar() {
      this.handoutQuestionToolbar = {
        visible: false,
        blockId: '',
        layout: 1,
        top: 0,
        left: 0
      }
      this.handoutQuestionHighlight = {
        visible: false,
        top: 0,
        left: 0,
        width: 0,
        height: 0
      }
    },
    applyHandoutQuestionOptionLayout(layout) {
      const root = this.getEditableRoot()
      const blockId = this.handoutQuestionToolbar.blockId
      if (!root || !blockId) return
      applyOptionLayoutToBlockInRoot(root, blockId, layout)
      const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
      const questionId = meta ? meta.getAttribute('data-question-id') : ''
      this._handoutPaginationPlan = upsertOptionLayoutInPlan(this._handoutPaginationPlan, blockId, questionId, layout)
      this.handoutQuestionToolbar = {
        ...this.handoutQuestionToolbar,
        layout: normalizeOptionLayout(layout)
      }
      this.flushModelSync()
      this.$nextTick(() => this.refreshHandoutQuestionSelectionVisual())
      this.$emit('option-layout-change', { blockId, layout: normalizeOptionLayout(layout) })
    },
    applyQuestionOptionLayout(blockId, layout) {
      const root = this.getEditableRoot()
      if (!root || !blockId) return false
      const ok = applyOptionLayoutToBlockInRoot(root, blockId, layout)
      if (!ok) return false
      const meta = root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
      const questionId = meta ? meta.getAttribute('data-question-id') : ''
      this._handoutPaginationPlan = upsertOptionLayoutInPlan(this._handoutPaginationPlan, blockId, questionId, layout)
      if (this.handoutQuestionToolbar.blockId === blockId) {
        this.handoutQuestionToolbar = {
          ...this.handoutQuestionToolbar,
          layout: normalizeOptionLayout(layout)
        }
        this.$nextTick(() => this.refreshHandoutQuestionSelectionVisual())
      }
      this.flushModelSync()
      return true
    },
    flushModelSync(force = false) {
      if (this._modelSyncTimer) {
        clearTimeout(this._modelSyncTimer)
        this._modelSyncTimer = null
      }
      if (!this.editorRef || this.previewOnly) return
      if (this.handoutMode && !force && this.isHandoutEditingFrozen()) return
      const raw = fixCosImageUrlsInHtml(cleanEditorHtml(this.editorRef.getHtml()))
      this.html = raw
      if (raw !== (this.value || '')) {
        this.$emit('input', raw)
        this.$emit('change', raw)
      }
    },
    scheduleModelSync() {
      if (this.handoutMode && this.isHandoutEditingFrozen()) return
      if (this._modelSyncTimer) clearTimeout(this._modelSyncTimer)
      this._modelSyncTimer = setTimeout(() => {
        this._modelSyncTimer = null
        this.flushModelSync()
      }, 400)
    },
    isEditorFocused() {
      const editable = this.editorRef && this.editorRef.getEditableContainer && this.editorRef.getEditableContainer()
      if (!editable) return false
      return !!(document.activeElement && editable.contains(document.activeElement))
    },
    isHandoutEditingFrozen() {
      return this.handoutMode && !this.previewOnly && (this.isEditorFocused() || this._handoutComposing)
    },
    getEditorScrollContainer() {
      if (!this.$el) return null
      return this.$el.querySelector('.w-e-scroll')
        || this.$el.querySelector('.w-e-text-container')
        || this.$refs.editorScroll
        || null
    },
    captureScrollState() {
      const el = this.getEditorScrollContainer()
      if (!el) return null
      return { el, top: el.scrollTop, left: el.scrollLeft }
    },
    restoreScrollState(state) {
      if (!state || !state.el) return
      state.el.scrollTop = state.top
      state.el.scrollLeft = state.left
    },
    flushHandoutEditorLayout(force = false) {
      if (!this.handoutMode) return
      if (!force && this.isHandoutEditingFrozen()) return
      if (this._handoutFullLayoutTimer) {
        clearTimeout(this._handoutFullLayoutTimer)
        this._handoutFullLayoutTimer = null
      }
      this.applyHandoutEditorLayout(force)
      this.$emit('handout-layout')
    },
    refreshHandoutPageVisuals() {
      if (!this.handoutMode) return null
      const root = this.getEditableRoot()
      if (!root) return null
      Array.from(root.querySelectorAll('p,div')).forEach(node => {
        this.decoratePageBreakNode(node)
      })
      root.classList.add('handout-body-surface')
      const titleOffsetPx = this.getHandoutTitleOffsetPx()
      root.style.setProperty('--handout-title-offset', `${titleOffsetPx}px`)
      const preliminary = computeHandoutPaginationPlan(root, { titleOffsetPx })
      this.layoutPageBreaks(root, preliminary)
      return computeHandoutPaginationPlan(root, { titleOffsetPx })
    },
    syncEditorHtml(html) {
      if (this.handoutMode && this.isHandoutEditingFrozen()) return
      const incomingPlan = extractHandoutPaginationPlan(html || '')
      this._handoutPaginationPlan = mergeIncomingHandoutPlan(this._handoutPaginationPlan, incomingPlan)
      const next = fixCosImageUrlsInHtml(cleanEditorHtml(stripHandoutPaginationComment(html || '')))
      this.html = next
      if (!this.editorRef) return
      this._suppressChange = true
      this.editorRef.setHtml(next)
      this.$nextTick(() => {
        this._suppressChange = false
        this.decorateHandoutEditor(true)
        const editRoot = this.getEditableRoot()
        if (editRoot && this._handoutPaginationPlan) {
          applyOptionLayoutsFromPlan(editRoot, this._handoutPaginationPlan)
        }
      })
    },
    handleChange() {
      if (this._suppressChange) return
      if (this.previewOnly) return
      if (this.handoutMode) {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.refreshPageBreakVisualsOnly())
        })
      }
      if (this.isHandoutEditingFrozen()) {
        this.scheduleModelSync()
        return
      }
      if (this.handoutMode) {
        this.ensureHandoutStructuralDecorations()
        this.scheduleHandoutLayout()
      }
      this.scheduleModelSync()
    },
    /** 分页符视觉装饰：编辑中也要刷新，不受 isHandoutEditingFrozen 限制 */
    refreshPageBreakVisualsOnly() {
      if (!this.handoutMode || this.previewOnly) return null
      const root = this.getEditableRoot()
      if (!root) return null
      Array.from(root.querySelectorAll('p,div')).forEach(node => {
        this.decoratePageBreakNode(node)
      })
      root.classList.add('handout-body-surface')
      const titleOffsetPx = this.getHandoutTitleOffsetPx()
      root.style.setProperty('--handout-title-offset', `${titleOffsetPx}px`)
      const preliminary = computeHandoutPaginationPlan(root, { titleOffsetPx })
      this.layoutPageBreaks(root, preliminary)
      return preliminary
    },
    getHtml() {
      if (this.editorRef) {
        const raw = stripHandoutPaginationComment(cleanEditorHtml(this.editorRef.getHtml()))
        this.html = raw
        return raw
      }
      return stripHandoutPaginationComment(cleanEditorHtml(this.html || ''))
    },
    buildHandoutPaginationSnapshot() {
      const root = this.getEditableRoot()
      this.captureAndMergeOptionLayoutPlan(root)
      this.decorateHandoutEditor(true)
      const titleOffsetPx = this.getHandoutTitleOffsetPx()
      let plan = root ? computeHandoutPaginationPlan(root, { titleOffsetPx }) : (this._handoutPaginationPlan || null)
      if (this._handoutPaginationPlan && this._handoutPaginationPlan.optionLayouts) {
        plan = mergeOptionLayoutsIntoPlan(plan, this._handoutPaginationPlan.optionLayouts)
        this._handoutPaginationPlan = plan
      }
      const raw = this.editorRef
        ? stripHandoutPaginationComment(cleanEditorHtml(this.editorRef.getHtml()))
        : stripHandoutPaginationComment(this.html || '')
      return embedHandoutPaginationComment(raw, plan)
    },
    buildHandoutContentSnapshot() {
      const root = this.getEditableRoot()
      this.captureAndMergeOptionLayoutPlan(root)
      const raw = this.editorRef
        ? stripHandoutPaginationComment(cleanEditorHtml(this.editorRef.getHtml()))
        : stripHandoutPaginationComment(this.html || '')
      return embedHandoutPaginationComment(raw, this._handoutPaginationPlan)
    },
    captureAndMergeOptionLayoutPlan(root) {
      let domLayoutMap = root ? collectOptionLayoutMapFromRoot(root) : { byBlockId: {}, byQuestionId: {} }
      if (root) {
        getQuestionBlockRangesFromRoot(root).forEach(item => {
          const inferred = inferLayoutFromRenderedOptions(item.metaNode, item.blockId)
          if (inferred !== 1) {
            domLayoutMap.byBlockId[item.blockId] = pickPreferredLayoutValue(domLayoutMap.byBlockId[item.blockId], inferred)
            if (item.questionId) {
              domLayoutMap.byQuestionId[item.questionId] = pickPreferredLayoutValue(
                domLayoutMap.byQuestionId[item.questionId],
                inferred
              )
            }
          }
        })
      }
      if (this.handoutQuestionToolbar.visible && this.handoutQuestionToolbar.blockId) {
        const blockId = this.handoutQuestionToolbar.blockId
        const layout = normalizeOptionLayout(this.handoutQuestionToolbar.layout)
        if (!domLayoutMap.byBlockId) domLayoutMap.byBlockId = {}
        domLayoutMap.byBlockId[blockId] = layout
        const meta = root && root.querySelector(`.handout-inline-question[data-handout-question-block="${blockId}"]`)
        const questionId = meta ? meta.getAttribute('data-question-id') : ''
        if (questionId) {
          if (!domLayoutMap.byQuestionId) domLayoutMap.byQuestionId = {}
          domLayoutMap.byQuestionId[questionId] = layout
        }
      }
      const existingLayouts = this._handoutPaginationPlan && this._handoutPaginationPlan.optionLayouts
      const layoutMap = mergeLayoutMapsPreferPlan(existingLayouts, domLayoutMap)
      if (layoutMap) {
        this._handoutPaginationPlan = mergeOptionLayoutsIntoPlan(this._handoutPaginationPlan, layoutMap)
      }
      return this._handoutPaginationPlan && this._handoutPaginationPlan.optionLayouts
    },
    moveHandoutQuestionBlockByIndex(fromIndex, toIndex, position) {
      const root = this.getEditableRoot()
      if (!root) return null
      const ranges = getQuestionBlockRangesFromRoot(root)
      if (!ranges.length) return null
      if (fromIndex < 0 || toIndex < 0 || fromIndex >= ranges.length || toIndex >= ranges.length) return null
      if (fromIndex === toIndex) return null

      this.captureAndMergeOptionLayoutPlan(root)
      const savedPlan = this._handoutPaginationPlan
        ? JSON.parse(JSON.stringify(this._handoutPaginationPlan))
        : null

      const sourceHtml = this.buildHandoutContentSnapshot()
      let nextHtml = moveQuestionBlockByIndexInHtml(sourceHtml, fromIndex, toIndex, position)
      if (!nextHtml || nextHtml === sourceHtml) return null

      let plan = extractHandoutPaginationPlan(nextHtml) || savedPlan || {}
      if (savedPlan && savedPlan.optionLayouts) {
        plan = {
          ...plan,
          optionLayouts: mergeLayoutMapsPreferPlan(plan.optionLayouts, savedPlan.optionLayouts)
        }
      }
      nextHtml = embedHandoutPaginationComment(stripHandoutPaginationComment(nextHtml), plan)
      nextHtml = ensureOptionLayoutsInHtml(renumberQuestionsInHtml(nextHtml))

      this._handoutPaginationPlan = extractHandoutPaginationPlan(nextHtml) || savedPlan
      this.syncEditorHtml(nextHtml)
      this.$nextTick(() => {
        this.flushHandoutEditorLayout()
      })
      return nextHtml
    },
    getHandoutTitleOffsetPx() {
      if (!this.handoutMode || !this.$el) return 0
      const banner = this.$el.querySelector('.handout-title-banner')
      return banner ? Math.round(banner.offsetHeight || 0) : 0
    },
    syncHandoutTitleBannerLayout() {
      if (!this.handoutMode || !this.$el) return
      const banner = this.$el.querySelector('.handout-title-banner')
      const root = this.getEditableRoot()
      const scrollEl = this.$refs.editorScroll
      if (!banner || !root || !scrollEl) return

      const rootRect = root.getBoundingClientRect()
      const scrollRect = scrollEl.getBoundingClientRect()
      const width = Math.max(0, Math.round(rootRect.width))
      if (!width) return

      const left = Math.round(rootRect.left - scrollRect.left + scrollEl.scrollLeft)
      banner.style.right = 'auto'
      banner.style.left = `${Math.max(0, left)}px`
      banner.style.width = `${width}px`
      banner.style.maxWidth = `${width}px`
      banner.style.textAlign = 'center'
    },
    bindHandoutBannerResizeSync() {
      if (!this.handoutMode || !this.previewOnly || typeof ResizeObserver === 'undefined') return
      this.unbindHandoutBannerResizeSync()
      const root = this.getEditableRoot()
      if (!root) return
      this._handoutBannerResizeObserver = new ResizeObserver(() => {
        this.syncHandoutTitleBannerLayout()
      })
      this._handoutBannerResizeObserver.observe(root)
      if (this.$refs.editorScroll) {
        this._handoutBannerResizeObserver.observe(this.$refs.editorScroll)
      }
    },
    unbindHandoutBannerResizeSync() {
      if (this._handoutBannerResizeObserver) {
        this._handoutBannerResizeObserver.disconnect()
        this._handoutBannerResizeObserver = null
      }
    },
    getText() {
      return this.editorRef ? this.editorRef.getText() : ''
    },
    clear() {
      if (this.editorRef) this.editorRef.clear()
    },
    setHtml(html) {
      this.syncEditorHtml(html)
    },
    insertHtml(html) {
      const snippet = fixCosImageUrlsInHtml(cleanEditorHtml(html || ''))
      if (!snippet) return
      if (this.editorRef && typeof this.editorRef.dangerouslyInsertHtml === 'function') {
        this.editorRef.focus()
        this.editorRef.dangerouslyInsertHtml(snippet)
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            this.flushHandoutEditorLayout(true)
            this.refreshPageBreakVisualsOnly()
            const raw = fixCosImageUrlsInHtml(cleanEditorHtml(this.editorRef.getHtml()))
            this.html = raw
            this.$emit('input', raw)
            this.$emit('change', raw)
          })
        })
        return
      }
      this.syncEditorHtml((this.html || '') + snippet)
      this.$emit('input', this.html)
      this.$emit('change', this.html)
    },
    focus() {
      if (this.editorRef) this.editorRef.focus()
    },
    getEditableRoot() {
      if (!this.editorRef || typeof this.editorRef.getEditableContainer !== 'function') return null
      const container = this.editorRef.getEditableContainer()
      if (!container) return null
      return container.querySelector('[data-slate-editor]') || container
    },
    handleHandoutMouseDown(event) {
      if (!this.handoutMode) return
      const node = this.getPageBreakNodeFromTarget(event.target)
      if (!node) {
        this.selectPageBreakNode(null)
        return
      }
      // 仅页间灰条/标签可交互；上方透明填充区不参与点击，避免误触换行
      if (!this.isPageBreakInteractionClick(node, event)) return
      event.preventDefault()
      this.selectPageBreakNode(node)
      if (this.editorRef) this.editorRef.focus()
      this.selectSlatePageBreakNode(node)
    },
    isPageBreakInteractionClick(node, event) {
      if (!node || !event) return false
      if (event.target && event.target.closest && event.target.closest('.handout-page-break-label')) {
        return true
      }
      const rect = node.getBoundingClientRect()
      if (!rect || !rect.height) return false
      const y = event.clientY - rect.top
      const style = window.getComputedStyle ? window.getComputedStyle(node) : null
      const fillHeight = style ? parseFloat(style.getPropertyValue('--handout-page-break-fill-height')) : 0
      const gapHeight = style ? parseFloat(style.getPropertyValue('--handout-page-gap')) : 34
      const fill = Number.isFinite(fillHeight) && fillHeight > 0 ? fillHeight : Math.max(0, rect.height - gapHeight)
      return y >= Math.max(0, fill - 2) && y <= fill + gapHeight + 6
    },
    onFormatPainterMouseDown(event) {
      if (this._formatPainterClickTimer) {
        clearTimeout(this._formatPainterClickTimer)
        this._formatPainterClickTimer = null
      }
      if (event.detail === 2) {
        if (this.formatPainterActive) this.cancelFormatPainter()
        this.formatPainterSticky = true
        this.activateFormatPainter()
        return
      }
      if (this.formatPainterActive) {
        this.cancelFormatPainter()
        return
      }
      this._formatPainterClickTimer = setTimeout(() => {
        this._formatPainterClickTimer = null
        if (this.formatPainterActive) return
        this.formatPainterSticky = false
        this.activateFormatPainter()
      }, 220)
    },
    activateFormatPainter() {
      if (!this.captureFormatPainter()) return
      this.formatPainterActive = true
      this.setupFormatPainterListener()
    },
    setupFormatPainterListener() {
      this.teardownFormatPainterListener()
      this._onFormatPainterSelectionChange = () => this.scheduleFormatPainterApply()
      document.addEventListener('selectionchange', this._onFormatPainterSelectionChange)
      this._onFormatPainterKeydown = (event) => {
        if (event.key === 'Escape') this.cancelFormatPainter()
      }
      document.addEventListener('keydown', this._onFormatPainterKeydown, true)
    },
    teardownFormatPainterListener() {
      if (this._onFormatPainterSelectionChange) {
        document.removeEventListener('selectionchange', this._onFormatPainterSelectionChange)
        this._onFormatPainterSelectionChange = null
      }
      if (this._onFormatPainterKeydown) {
        document.removeEventListener('keydown', this._onFormatPainterKeydown, true)
        this._onFormatPainterKeydown = null
      }
    },
    cancelFormatPainter() {
      this.teardownFormatPainterListener()
      if (this._formatPainterApplyTimer) {
        clearTimeout(this._formatPainterApplyTimer)
        this._formatPainterApplyTimer = null
      }
      this.formatPainterActive = false
      this.formatPainterSticky = false
      this.formatPainterCaptured = null
      this._formatPainterSourceKey = ''
    },
    serializeSelection(selection) {
      if (!selection) return ''
      return JSON.stringify({ anchor: selection.anchor, focus: selection.focus })
    },
    syncEditorSelectionFromDom() {
      const editor = this.editorRef
      if (!editor) return false
      const root = this.getEditableRoot()
      const sel = window.getSelection && window.getSelection()
      if (!root || !sel || !sel.rangeCount) return false
      const domRange = sel.getRangeAt(0)
      if (!root.contains(domRange.commonAncestorContainer)) return false
      try {
        const slateRange = DomEditor.toSlateRange(editor, domRange, { exactMatch: false, suppressThrow: true })
        if (slateRange && DomEditor.hasRange(editor, slateRange)) {
          SlateTransforms.select(editor, slateRange)
          return true
        }
      } catch (e) {
        // ignore sync errors
      }
      return false
    },
    captureMarksFromDomRange(range) {
      const marks = {}
      if (!range) return marks
      let element = range.startContainer
      if (element && element.nodeType === 3) element = element.parentElement
      if (!element || !element.closest) return marks
      const block = element.closest('h1,h2,h3,h4,h5,p,span,strong,em,u,s')
      const target = block || element
      const style = window.getComputedStyle ? window.getComputedStyle(target) : null
      if (!style) return marks
      const weight = style.fontWeight || ''
      if (weight === 'bold' || Number(weight) >= 600) marks.bold = true
      if (style.fontStyle === 'italic') marks.italic = true
      const deco = style.textDecorationLine || style.textDecoration || ''
      if (deco.includes('underline')) marks.underline = true
      if (deco.includes('line-through')) marks.through = true
      if (style.color) marks.color = style.color
      if (style.backgroundColor && style.backgroundColor !== 'rgba(0, 0, 0, 0)' && style.backgroundColor !== 'transparent') {
        marks.bgColor = style.backgroundColor
      }
      if (style.fontSize) marks.fontSize = style.fontSize
      if (style.fontFamily) {
        marks.fontFamily = style.fontFamily.split(',')[0].replace(/['"]/g, '').trim()
      }
      return marks
    },
    captureBlockFromDomRange(range) {
      const result = { blockType: 'paragraph', blockProps: {} }
      if (!range) return result
      let element = range.startContainer
      if (element && element.nodeType === 3) element = element.parentElement
      if (!element || !element.closest) return result
      const block = element.closest('h1,h2,h3,h4,h5,p')
      if (!block) return result
      const tag = (block.tagName || 'P').toUpperCase()
      if (tag.startsWith('H') && tag.length === 2) {
        result.blockType = `header${tag.charAt(1)}`
      }
      const style = window.getComputedStyle ? window.getComputedStyle(block) : null
      if (style && style.textAlign) result.blockProps.textAlign = style.textAlign
      if (style && style.lineHeight) result.blockProps.lineHeight = style.lineHeight
      return result
    },
    captureFormatPainter() {
      const editor = this.editorRef
      if (!editor || editor.isDisabled()) return false
      editor.focus()
      this.syncEditorSelectionFromDom()
      const { selection } = editor
      if (!selection || SlateRange.isCollapsed(selection)) {
        if (this.$message) this.$message.warning('请先选中要复制格式的文字')
        return false
      }
      let marks = { ...(SlateEditor.marks(editor) || {}) }
      const domRange = this.getEditorSelectionRange()
      if (domRange) {
        marks = { ...this.captureMarksFromDomRange(domRange), ...marks }
      }
      const elems = DomEditor.getSelectedElems(editor)
      const block = elems.length ? elems[elems.length - 1] : null
      let blockType = block && block.type ? block.type : 'paragraph'
      const blockProps = {}
      if (block && block.textAlign) blockProps.textAlign = block.textAlign
      if (block && block.lineHeight) blockProps.lineHeight = block.lineHeight
      if (block && block.indent) blockProps.indent = block.indent
      if (domRange) {
        const domBlock = this.captureBlockFromDomRange(domRange)
        if (domBlock.blockType) blockType = domBlock.blockType
        Object.assign(blockProps, domBlock.blockProps)
      }
      this.formatPainterCaptured = { marks, blockType, blockProps }
      this._formatPainterSourceKey = this.serializeSelection(selection)
      return true
    },
    scheduleFormatPainterApply() {
      if (!this.formatPainterActive || !this.formatPainterCaptured) return
      if (this._formatPainterApplyTimer) clearTimeout(this._formatPainterApplyTimer)
      this._formatPainterApplyTimer = setTimeout(() => {
        this._formatPainterApplyTimer = null
        this.tryApplyFormatPainter()
      }, 120)
    },
    tryApplyFormatPainter() {
      if (!this.formatPainterActive || !this.formatPainterCaptured) return false
      const editor = this.editorRef
      if (!editor) return false
      editor.focus()
      this.syncEditorSelectionFromDom()
      const { selection } = editor
      if (!selection || SlateRange.isCollapsed(selection)) return false
      const key = this.serializeSelection(selection)
      if (!key || key === this._formatPainterSourceKey) return false
      const applied = this.applyFormatPainter()
      if (applied && this.formatPainterSticky) {
        this._formatPainterSourceKey = key
      }
      return applied
    },
    applyFormatPainter() {
      const editor = this.editorRef
      const captured = this.formatPainterCaptured
      if (!editor || !captured || !this.formatPainterActive) return false
      this.syncEditorSelectionFromDom()
      const { selection } = editor
      if (!selection || SlateRange.isCollapsed(selection)) return false
      const { marks, blockType, blockProps } = captured
      try {
        editor.focus()
        if (blockType && FORMAT_BLOCK_TYPES.includes(blockType)) {
          SlateTransforms.setNodes(
            editor,
            { type: blockType, ...blockProps },
            {
              match: n => SlateElement.isElement(n) && FORMAT_BLOCK_TYPES.includes(String(n.type)),
              mode: 'highest'
            }
          )
        }
        FORMAT_MARK_KEYS.forEach((key) => {
          const value = marks[key]
          if (value != null && value !== '' && value !== false) {
            SlateEditor.addMark(editor, key, value)
          } else {
            SlateEditor.removeMark(editor, key)
          }
        })
        this.triggerEditorModelUpdate()
      } catch (e) {
        return false
      }
      if (!this.formatPainterSticky) {
        this.cancelFormatPainter()
      }
      return true
    },
    handleHandoutMouseUp() {
      if (!this.handoutMode || !this.formatPainterActive || !this.formatPainterCaptured) return
      this.scheduleFormatPainterApply()
    },
    handleHandoutKeydown(event) {
      if (!this.handoutMode) return
      if (event.key === 'Escape' && this.formatPainterActive) {
        event.preventDefault()
        event.stopPropagation()
        this.cancelFormatPainter()
        return
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        const range = this.getEditorSelectionRange()
        const block = range ? this.getCurrentEditableBlock(range) : null
        if (block && /^H[1-6]$/i.test(block.tagName) && this.isCaretAtBlockEdge(block, range, 'start')) {
          if (this.insertParagraphBeforeBlockAtCaret()) {
            event.preventDefault()
            return
          }
        }
      }
      if (event.key !== 'Backspace' && event.key !== 'Delete') {
        this.selectPageBreakNode(null)
        return
      }
      const selected = this._selectedPageBreakNode
      const direct = this.getPageBreakNodeFromSelection() || this.getPageBreakNodeNearCaret(event.key)
      const target = selected && selected.isConnected ? selected : direct
      if (!target) return
      event.preventDefault()
      this.removePageBreakNode(target)
    },
    insertParagraphBeforeBlockAtCaret() {
      if (!this.editorRef) return false
      const range = this.getEditorSelectionRange()
      if (!range || !range.collapsed) return false
      const block = this.getCurrentEditableBlock(range)
      if (!block || this.getPageBreakNodeFromTarget(block) || block.closest('table')) return false
      if (!this.isCaretAtBlockEdge(block, range, 'start')) return false
      try {
        const slateNode = DomEditor.toSlateNode(this.editorRef, block)
        const path = DomEditor.findPath(this.editorRef, slateNode)
        const paragraph = { type: 'paragraph', children: [{ text: '' }] }
        SlateTransforms.insertNodes(this.editorRef, paragraph, { at: path })
        SlateTransforms.select(this.editorRef, { path: path.concat(0), offset: 0 })
        this.editorRef.focus()
        this.triggerEditorModelUpdate()
        return true
      } catch (e) {
        return this.insertDomParagraphBeforeBlock(block)
      }
    },
    insertDomParagraphBeforeBlock(block) {
      if (!block || !block.parentNode) return false
      const p = document.createElement('p')
      p.innerHTML = '<br>'
      block.parentNode.insertBefore(p, block)
      const selection = window.getSelection && window.getSelection()
      if (selection) {
        const range = document.createRange()
        range.selectNodeContents(p)
        range.setStart(p, 0)
        range.collapse(true)
        selection.removeAllRanges()
        selection.addRange(range)
      }
      this.triggerEditorModelUpdate()
      return true
    },
    getCurrentEditableBlock(range) {
      const element = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement
      return element && element.closest && element.closest('p,div,h1,h2,h3,h4,h5,h6,li,blockquote')
    },
    getPageBreakNodeFromTarget(target) {
      if (!target || !target.closest) return null
      const node = target.closest('.handout-page-break-token,[data-handout-page-break="true"]')
      const root = this.getEditableRoot()
      return node && root && root.contains(node) ? node : null
    },
    selectPageBreakNode(node) {
      if (this._selectedPageBreakNode && this._selectedPageBreakNode !== node) {
        this._selectedPageBreakNode.classList.remove('is-selected')
      }
      this._selectedPageBreakNode = node || null
      if (node) node.classList.add('is-selected')
    },
    selectSlatePageBreakNode(node) {
      if (!this.editorRef || !node) return
      try {
        const slateNode = DomEditor.toSlateNode(this.editorRef, node)
        const path = DomEditor.findPath(this.editorRef, slateNode)
        SlateTransforms.select(this.editorRef, path)
      } catch (e) {
        // DOM selection is enough for the visual selected state.
      }
    },
    getEditorSelectionRange() {
      const root = this.getEditableRoot()
      const selection = window.getSelection && window.getSelection()
      if (!root || !selection || !selection.rangeCount) return null
      const range = selection.getRangeAt(0)
      return root.contains(range.commonAncestorContainer) ? range : null
    },
    getPageBreakNodeFromSelection() {
      const range = this.getEditorSelectionRange()
      if (!range) return null
      const startNode = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement
      const endNode = range.endContainer.nodeType === 1 ? range.endContainer : range.endContainer.parentElement
      return this.getPageBreakNodeFromTarget(startNode) || this.getPageBreakNodeFromTarget(endNode)
    },
    getPageBreakNodeNearCaret(key) {
      const range = this.getEditorSelectionRange()
      if (!range || !range.collapsed) return null
      const block = this.getCurrentEditableBlock(range)
      if (!block) return null
      if (key === 'Backspace' && this.isCaretAtBlockEdge(block, range, 'start')) {
        return this.findSiblingPageBreak(block, 'previousElementSibling')
      }
      if (key === 'Delete' && this.isCaretAtBlockEdge(block, range, 'end')) {
        return this.findSiblingPageBreak(block, 'nextElementSibling')
      }
      return null
    },
    isCaretAtBlockEdge(block, range, edge) {
      const probe = document.createRange()
      probe.selectNodeContents(block)
      if (edge === 'start') {
        probe.setEnd(range.startContainer, range.startOffset)
      } else {
        probe.setStart(range.startContainer, range.startOffset)
      }
      return !probe.toString().replace(/[\u200b\ufeff\s]/g, '')
    },
    findSiblingPageBreak(block, property) {
      let node = block[property]
      while (node) {
        if (node.nodeType === 1 && node.matches('.handout-page-break-token,[data-handout-page-break="true"]')) return node
        const text = (node.textContent || '').replace(/[\u200b\ufeff\s]/g, '')
        if (text || (node.querySelector && node.querySelector('img,table'))) return null
        node = node[property]
      }
      return null
    },
    removePageBreakNode(node) {
      if (!node || !this.editorRef) return
      try {
        const slateNode = DomEditor.toSlateNode(this.editorRef, node)
        const path = DomEditor.findPath(this.editorRef, slateNode)
        SlateTransforms.removeNodes(this.editorRef, { at: path })
      } catch (e) {
        node.remove()
      }
      this.selectPageBreakNode(null)
      this.triggerEditorModelUpdate()
    },
    triggerEditorModelUpdate() {
      if (this.handoutMode && this.isHandoutEditingFrozen()) return
      this.$nextTick(() => {
        this.decorateHandoutEditor(true)
        if (!this.editorRef) return
        const raw = fixCosImageUrlsInHtml(cleanEditorHtml(this.editorRef.getHtml()))
        this.html = raw
        this.$emit('input', raw)
        this.$emit('change', raw)
      })
    },
    ensureHandoutHeadingClasses() {
      if (!this.handoutMode) return
      const root = this.getEditableRoot()
      if (!root) return
      root.classList.add('handout-heading-scope')
      Array.from(root.querySelectorAll('h1')).forEach((node) => {
        node.classList.add('handout-heading-1')
      })
    },
    decorateHandoutEditor(force = false) {
      if (!this.handoutMode || this._handoutDecorating) return
      if (!force && this.isHandoutEditingFrozen()) return
      this._handoutDecorating = true
      try {
        this.ensureHandoutStructuralDecorations()
        this.applyHandoutEditorLayout(force)
      } finally {
        this._handoutDecorating = false
      }
    },
    scheduleHandoutLayout() {
      if (!this.handoutMode || this.previewOnly || this.isHandoutEditingFrozen()) return
      if (this._handoutFullLayoutTimer) {
        clearTimeout(this._handoutFullLayoutTimer)
      }
      this._handoutFullLayoutTimer = setTimeout(() => {
        this._handoutFullLayoutTimer = null
        if (this.isHandoutEditingFrozen()) return
        this.refreshHandoutPageVisuals()
      }, 500)
    },
    captureEditorSelection() {
      const editor = this.editorRef
      if (!editor || !editor.selection) return null
      try {
        return JSON.parse(JSON.stringify(editor.selection))
      } catch (e) {
        return null
      }
    },
    restoreEditorSelection(snapshot) {
      const editor = this.editorRef
      if (!editor || !snapshot) return
      const scrollState = this.captureScrollState()
      this.$nextTick(() => {
        try {
          if (DomEditor.hasRange(editor, snapshot)) {
            SlateTransforms.select(editor, snapshot)
          }
          this.restoreScrollState(scrollState)
        } catch (e) {}
      })
    },
    applyHandoutEditorLayout(force = false) {
      if (!this.handoutMode) return
      if (!force && this.isHandoutEditingFrozen()) return
      const root = this.getEditableRoot()
      if (!root) return
      root.classList.toggle('hide-question-source', !this.showQuestionSource)
      root.classList.toggle('hide-question-difficulty', !this.showQuestionDifficulty)
      root.classList.add('handout-heading-scope')
      root.classList.add('handout-body-surface')
      Array.from(root.querySelectorAll('p,div')).forEach(node => {
        this.decoratePageBreakNode(node)
      })
      Array.from(root.querySelectorAll('h1')).forEach(node => {
        node.classList.add('handout-heading-1')
      })
      this.applyQuestionMetaVisibility(root)
      const titleOffsetPx = this.getHandoutTitleOffsetPx()
      const plan = this.refreshHandoutPageVisuals() || computeHandoutPaginationPlan(root, { titleOffsetPx })
      this.syncHandoutTitleBannerLayout()
      if (this.previewOnly) {
        this.bindHandoutBannerResizeSync()
        const scrollEl = this.$refs.editorScroll
        const surfaceHeight = root ? Math.ceil(root.scrollHeight + 24) : 400
        this.previewEditorHeight = surfaceHeight
        Array.from(root.querySelectorAll('img')).forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', () => this.scheduleHandoutLayout(), { once: true })
          }
        })
        if (this._handoutPaginationPlan) {
          applyOptionLayoutsFromPlan(root, this._handoutPaginationPlan)
        } else {
          ensureOptionLayoutsInRoot(root, null)
        }
        this.syncPreviewPageNumbers(root, plan)
        this.$emit('handout-layout', {
          height: scrollEl ? Math.ceil(scrollEl.offsetHeight) : surfaceHeight + titleOffsetPx,
          pageCount: plan ? plan.pageCount : 1,
          pageHeight: plan ? plan.pageHeight : calcHandoutPageHeight(root.clientWidth || 760),
          pageGap: plan ? plan.pageGap : 34,
          titleOffsetPx,
          contentWidth: plan ? plan.contentWidth : Math.round(root.clientWidth || 0)
        })
        return
      }
    },
    ensureHandoutStructuralDecorations() {
      if (!this.handoutMode) return
      const root = this.getEditableRoot()
      if (!root) return
      Array.from(root.querySelectorAll('p,div')).forEach(node => {
        this.decoratePageBreakNode(node)
        this.decorateQuestionMetaNode(node)
      })
      ensureQuestionBlockIds(root)
      stripHandoutQuestionKnowledgeSpans(root)
      renumberInlineQuestionIndices(root)
      const layoutMap = this._handoutPaginationPlan && this._handoutPaginationPlan.optionLayouts
        ? this._handoutPaginationPlan.optionLayouts
        : null
      ensureOptionLayoutsInRoot(root, layoutMap)
      root.classList.toggle('hide-question-source', !this.showQuestionSource)
      root.classList.toggle('hide-question-difficulty', !this.showQuestionDifficulty)
      this.applyQuestionMetaVisibility(root, true)
    },
    decorateQuestionMetaNode(node) {
      if (!node || node.closest('table')) return
      const text = (node.textContent || '').replace(/\s+/g, ' ').trim()
      if (!isQuestionMetaText(text)) return
      const typeSpan = node.querySelector('[data-handout-question-type], .handout-question-type')
      const knowledgeSpan = node.querySelector('[data-handout-question-knowledge], .handout-question-knowledge')
      const sourceSpan = node.querySelector('[data-handout-question-source], .handout-question-source')
      const difficultySpan = node.querySelector('[data-handout-question-difficulty], .handout-question-difficulty')
      const sourceText = sourceSpan ? (sourceSpan.textContent || '') : ''
      const typeText = typeSpan ? (typeSpan.textContent || '') : ''
      const needsRebuild = !typeSpan && !knowledgeSpan && !sourceSpan && !difficultySpan
        || (!typeSpan && sourceSpan && /来源[：:]/.test(sourceText))
        || (sourceSpan && /(单选题|多选题|填空题|问答题|选择题)/.test(sourceText) && /来源[：:]/.test(sourceText))
        || (typeSpan && !knowledgeSpan && QUESTION_TYPE_RE.test(typeText) && typeText.includes('/'))
        || (typeSpan && /^\d+\.?\s*/.test((typeText || '').trim()))
      if (!needsRebuild) {
        node.classList.add('handout-inline-question')
        node.removeAttribute('data-handout-meta-normalized')
        cleanupStrayQuestionIndexNodes(node)
        if (typeSpan) {
          const cleaned = stripLeadingQuestionIndex((typeSpan.textContent || '').trim())
          if (cleaned !== (typeSpan.textContent || '').trim()) {
            typeSpan.textContent = cleaned
          }
        }
        return
      }
      const prefix = extractQuestionMetaPrefix(node)
      const savedLayout = node.getAttribute('data-handout-option-layout')
      const savedBlockId = node.getAttribute('data-handout-question-block')
      const savedQuestionId = node.getAttribute('data-question-id')
      const savedTitle = node.getAttribute('data-question-title')
      node.classList.add('handout-inline-question')
      node.removeAttribute('data-handout-meta-normalized')
      node.innerHTML = prefix + buildQuestionMetaInnerHtml(splitQuestionMetaLine(text), this.escapeHtml.bind(this))
      if (savedLayout) node.setAttribute('data-handout-option-layout', savedLayout)
      if (savedBlockId) node.setAttribute('data-handout-question-block', savedBlockId)
      if (savedQuestionId) node.setAttribute('data-question-id', savedQuestionId)
      if (savedTitle) node.setAttribute('data-question-title', savedTitle)
    },
    applyQuestionMetaVisibility(root, force = false) {
      const cacheKey = `${this.showQuestionSource ? 1 : 0}:${this.showQuestionDifficulty ? 1 : 0}`
      if (!force && root.getAttribute('data-handout-meta-visibility') === cacheKey) return
      root.setAttribute('data-handout-meta-visibility', cacheKey)
      stripHandoutQuestionKnowledgeSpans(root)
      Array.from(root.querySelectorAll('.handout-inline-question')).forEach(node => {
        const sourceNodes = Array.from(node.querySelectorAll('[data-handout-question-source],.handout-question-source'))
        const difficultyNodes = Array.from(node.querySelectorAll('[data-handout-question-difficulty],.handout-question-difficulty'))
        sourceNodes.forEach(item => { item.style.display = this.showQuestionSource ? '' : 'none' })
        difficultyNodes.forEach(item => { item.style.display = this.showQuestionDifficulty ? '' : 'none' })
        node.style.removeProperty('display')
      })
    },
    decoratePageBreakNode(node) {
      if (!node || node.closest('table')) return
      const text = (node.textContent || '').replace(/\s+/g, '').trim()
      const hasLabel = node.querySelector && node.querySelector('.handout-page-break-label')
      const marked = node.hasAttribute('data-handout-page-break') ||
        (node.classList && node.classList.contains('handout-page-break-token')) ||
        text.includes('【分页符】') ||
        text === '分页符' ||
        !!hasLabel
      if (!marked) return
      node.classList.add('handout-page-break-token')
      node.setAttribute('data-handout-page-break', 'true')
      node.setAttribute('tabindex', '0')
      node.setAttribute('title', '分页符')
      if (!hasLabel) {
        node.innerHTML = '<span class="handout-page-break-label">分页符</span>'
      }
    },
    syncPreviewPageNumbers(root, plan) {
      const scrollEl = this.$refs.editorScroll
      if (!scrollEl) return

      Array.from(scrollEl.querySelectorAll('.handout-preview-page-number-layer')).forEach((node) => node.remove())
      Array.from(root.querySelectorAll('.handout-preview-page-number')).forEach((node) => node.remove())

      if (!this.previewOnly || !this.showPageNumbers || !plan || !root) return

      const layer = document.createElement('div')
      layer.className = 'handout-preview-page-number-layer'
      layer.setAttribute('aria-hidden', 'true')

      const positions = buildHandoutPreviewPageNumberPositions(root, scrollEl, plan)
      const last = positions[positions.length - 1]
      layer.style.height = last ? `${last.top + 24}px` : `${scrollEl.scrollHeight}px`

      positions.forEach(({ page, top }) => {
        const marker = document.createElement('div')
        marker.className = 'handout-preview-page-number'
        marker.setAttribute('data-handout-page-index', String(page))
        marker.textContent = String(page)
        marker.style.top = `${top}px`
        layer.appendChild(marker)
      })

      scrollEl.appendChild(layer)
    },
    layoutPageBreaks(root, plan) {
      const scrollState = this.previewOnly ? null : this.captureScrollState()
      const pageHeight = plan && plan.pageHeight
        ? plan.pageHeight
        : calcHandoutPageHeight(root.clientWidth || 760)
      const pageGap = plan && plan.pageGap ? plan.pageGap : 34
      root.style.setProperty('--handout-page-height', `${pageHeight}px`)
      root.style.setProperty('--handout-page-gap', `${pageGap}px`)
      let consumedPageGaps = 0
      Array.from(root.querySelectorAll('.handout-page-break-token')).forEach(node => {
        const contentTop = Math.max(0, (node.offsetTop || 0) - consumedPageGaps)
        const offsetInPage = contentTop % pageHeight
        const remaining = Math.max(120, pageHeight - offsetInPage)
        const breakHeight = remaining + pageGap
        node.style.setProperty('--handout-page-break-height', `${breakHeight}px`)
        node.style.setProperty('--handout-page-break-fill-height', `${remaining}px`)
        consumedPageGaps += pageGap
      })
      applyHandoutPageBackground(root, plan || { pageHeight, pageCount: 1 })
      if (scrollState) {
        requestAnimationFrame(() => this.restoreScrollState(scrollState))
      }
    },
    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    }
  }
}
</script>

<style scoped>
.rich-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: visible;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.editor-toolbar-wrap {
  display: flex;
  align-items: flex-start;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
  flex-shrink: 0;
  overflow: visible;
  position: relative;
  z-index: 30;
}

.format-painter-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  margin: 4px 0 4px 6px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #595959;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.format-painter-btn:hover {
  background: #f5f5f5;
  color: #1677ff;
}

.format-painter-btn.active {
  background: #e6f4ff;
  border-color: #91caff;
  color: #1677ff;
}

.format-painter-btn.sticky {
  box-shadow: inset 0 0 0 1px #1677ff;
}

.format-painter-icon {
  width: 18px;
  height: 18px;
}

.editor-toolbar {
  flex: 1;
  min-width: 0;
  border-bottom: none;
  flex-shrink: 0;
  background: #fff;
}

.rich-editor.format-painter-active ::v-deep .w-e-text-container [data-slate-editor] {
  cursor: copy;
}

.editor-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.editor-scroll {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.handout-title-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  flex-shrink: 0;
  padding: 20px 32px 16px;
  background: #fff;
  color: #1677ff;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
  word-break: break-word;
  pointer-events: none;
  box-sizing: border-box;
}

.rich-editor.handout-mode .editor-scroll {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
}

.rich-editor.handout-mode .editor-scroll > div {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.rich-editor ::v-deep .w-e-text-container {
  background-color: transparent;
  direction: ltr !important;
  text-align: left !important;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] {
  direction: ltr !important;
  text-align: left !important;
  unicode-bidi: normal !important;
  min-height: 200px;
  font-family: '微软雅黑', 'Microsoft YaHei', sans-serif;
  font-size: 12pt;
  line-height: 1.5;
  color: #222;
}

.rich-editor ::v-deep [contenteditable="true"] {
  spellcheck: false;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] img {
  max-width: 100%;
  height: auto;
  vertical-align: middle;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] table img {
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
  max-width: 100% !important;
  max-height: none !important;
  width: auto !important;
  height: auto !important;
  min-width: 24px;
  min-height: 24px;
  object-fit: contain;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] [data-handout-question-block] img {
  max-height: 80px;
  width: auto;
  max-width: 100%;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] .table-container {
  overflow-x: auto;
  max-width: 100%;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] table {
  display: inline-table;
  border-collapse: collapse;
  width: auto !important;
  max-width: none;
  table-layout: auto !important;
  vertical-align: top;
  border: 1px solid #333;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] table td,
.rich-editor ::v-deep .w-e-text-container [data-slate-editor] table th {
  border: 1px solid #333;
  padding: 6px 8px;
  min-width: 40px;
  line-height: 1.6;
  vertical-align: top;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
  width: auto !important;
}

.rich-editor ::v-deep .w-e-text-container [data-slate-editor] table th {
  background: #f0f0f0;
  font-weight: 600;
  text-align: center;
}

.rich-editor ::v-deep .w-e-toolbar {
  background-color: #fafafa;
  flex-wrap: wrap;
  height: auto !important;
  min-height: 40px;
  overflow: visible !important;
}

.rich-editor ::v-deep .w-e-bar {
  flex-wrap: wrap;
  height: auto !important;
  overflow: visible !important;
}

.rich-editor ::v-deep .w-e-bar-item {
  overflow: visible !important;
}

.rich-editor ::v-deep .w-e-select-list {
  z-index: 3000 !important;
}

.rich-editor ::v-deep .w-e-bar-item button {
  padding: 4px 6px;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] {
  counter-reset: handout-h1;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h1,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-heading-1 {
  counter-increment: handout-h1;
  display: block !important;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  background: #e8f3ff !important;
  color: #1677ff !important;
  font-size: 18px !important;
  font-weight: 700 !important;
  line-height: 1.45 !important;
  padding: 8px 16px 8px 40px !important;
  border-radius: 8px !important;
  margin: 20px 0 12px !important;
  position: relative !important;
  min-height: 38px !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h1::before,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-heading-1::before {
  content: counter(handout-h1) !important;
  display: block !important;
  position: absolute !important;
  left: 10px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  width: 22px !important;
  height: 22px !important;
  border-radius: 50% !important;
  background: #1677ff !important;
  color: #fff !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 22px !important;
  text-align: center !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h1 span,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h1 [data-slate-string],
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-heading-1 span,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-heading-1 [data-slate-string] {
  color: #1677ff !important;
  font-size: 18px !important;
  font-weight: 700 !important;
  line-height: 1.45 !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h2 {
  font-size: 16px !important;
  font-weight: 700 !important;
  color: #1f2937 !important;
  margin: 14px 0 8px !important;
  line-height: 1.5 !important;
  padding-left: 4px !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h3 {
  font-size: 15px !important;
  font-weight: 700 !important;
  color: #374151 !important;
  margin: 12px 0 6px !important;
  line-height: 1.5 !important;
  padding-left: 8px !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] h4 {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #4b5563 !important;
  margin: 10px 0 4px !important;
  line-height: 1.5 !important;
  padding-left: 12px !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container {
  background: #eef1f5;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] {
  background-color: #fff;
  background-image: repeating-linear-gradient(
    to bottom,
    #fff 0,
    #fff calc(var(--handout-page-height, 900px) - 1px),
    #e8ebf0 calc(var(--handout-page-height, 900px) - 1px),
    #e8ebf0 var(--handout-page-height, 900px)
  );
  position: relative;
  box-sizing: border-box;
  min-height: var(--handout-page-height, 900px);
  padding-top: var(--handout-title-offset, 0px);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12), inset 0 0 0 1px #e3e8f0;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor].handout-body-surface,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] {
  font-family: '微软雅黑', 'Microsoft YaHei', sans-serif;
  font-size: 12pt;
  line-height: 1.5;
  color: #222;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-inline-question {
  color: #4b5563;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token {
  display: block !important;
  height: var(--handout-page-break-height, 180px) !important;
  min-height: 140px !important;
  width: 100%;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  color: transparent;
  font-size: 0;
  line-height: 0;
  overflow: visible;
  background: linear-gradient(
    to bottom,
    transparent 0,
    transparent var(--handout-page-break-fill-height, 120px),
    #e8ebf0 var(--handout-page-break-fill-height, 120px),
    #e8ebf0 100%
  ) !important;
  line-height: 1;
  box-sizing: border-box;
  position: relative;
  user-select: none;
  cursor: default;
  outline: none;
  pointer-events: none;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token::before,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token::after {
  content: '';
  position: absolute;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token::before {
  left: -32px;
  right: -32px;
  bottom: 0;
  height: var(--handout-page-gap, 34px);
  border-top: 1px solid #cfd5df;
  border-bottom: 1px solid #cfd5df;
  background: #e8ebf0;
  box-shadow: inset 0 7px 9px rgba(15, 23, 42, 0.08), inset 0 -7px 9px rgba(15, 23, 42, 0.06);
  pointer-events: auto;
  cursor: default;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token::after {
  left: -1px;
  right: -1px;
  top: calc(var(--handout-page-break-fill-height, 120px) - 1px);
  border-top: 1px solid #d5dbe5;
  box-shadow: 0 3px 6px rgba(15, 23, 42, 0.10);
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token > *:not(.handout-page-break-label),
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] [data-handout-page-break="true"] > *:not(.handout-page-break-label) {
  display: none !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token span,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token .handout-page-break-label {
  position: absolute;
  z-index: 1;
  left: 50%;
  bottom: 7px;
  transform: translateX(-50%);
  padding: 2px 10px;
  border: 1px solid #d8dde7;
  border-radius: 999px;
  background: #fff;
  color: #697386;
  font-size: 12px;
  line-height: 1.4;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  pointer-events: auto;
  cursor: default;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token.is-selected::before {
  border-color: #409eff;
  box-shadow: inset 0 8px 12px rgba(64, 158, 255, 0.12), inset 0 -8px 12px rgba(64, 158, 255, 0.1), 0 0 0 2px rgba(64, 158, 255, 0.16);
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token.is-selected span,
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token.is-selected .handout-page-break-label {
  border-color: #409eff;
  color: #1677ff;
}

.rich-editor.preview-only {
  border: none;
  border-radius: 0;
  height: auto;
  overflow: visible;
}

.rich-editor.preview-only .editor-body {
  flex: none;
  overflow: visible;
}

.rich-editor.handout-mode.preview-only .editor-scroll {
  position: relative;
  overflow: visible;
}

.rich-editor.preview-only ::v-deep .w-e-text-container {
  height: auto !important;
}

.rich-editor.preview-only ::v-deep .w-e-scroll {
  height: auto !important;
  overflow: visible !important;
}

/* PDF 导出：须覆盖上方 handout-mode 的 !important 渐变/阴影，避免 html2canvas 截进灰条 */
.rich-editor.handout-pdf-export.handout-mode .handout-title-banner {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: auto !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  text-align: center !important;
  direction: ltr !important;
  unicode-bidi: normal !important;
  box-sizing: border-box !important;
  background: #fff !important;
}

.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container {
  background: #fff !important;
}

.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] {
  background-color: #fff !important;
  background-image: none !important;
  box-shadow: none !important;
}

.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token,
.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] [data-handout-page-break="true"] {
  background: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
}

.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token::before,
.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-page-break-token::after,
.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] [data-handout-page-break="true"]::before,
.rich-editor.handout-pdf-export.handout-mode ::v-deep .w-e-text-container [data-slate-editor] [data-handout-page-break="true"]::after {
  display: none !important;
  content: none !important;
  box-shadow: none !important;
  background: transparent !important;
  border: none !important;
}

.handout-pdf-export .handout-page-break-label {
  display: none !important;
}

.handout-pdf-export .handout-page-numbers-overlay {
  display: none !important;
}

.handout-pdf-export .handout-preview-page-number-layer {
  display: none !important;
}

.handout-pdf-export .handout-preview-page-number {
  display: none !important;
}

.rich-editor.handout-mode .editor-scroll {
  position: relative;
}

.handout-question-block-highlight {
  position: absolute;
  z-index: 20;
  pointer-events: none;
  border: 2px solid #1677ff;
  border-radius: 10px;
  background: rgba(22, 119, 255, 0.06);
  box-shadow: 0 0 0 1px rgba(22, 119, 255, 0.15), 0 8px 20px rgba(22, 119, 255, 0.12);
}

.handout-question-toolbar {
  position: absolute;
  z-index: 40;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #cfd8ea;
  border-radius: 10px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.18);
  max-width: calc(100% - 16px);
  flex-wrap: wrap;
}

.handout-question-toolbar-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
}

.handout-question-toolbar-divider {
  width: 1px;
  height: 18px;
  background: #dbe3f0;
}

.handout-question-toolbar-label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.handout-option-layout-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #374151;
  font-size: 12px;
  line-height: 1;
  padding: 5px 8px;
  cursor: pointer;
}

.handout-option-layout-btn.active {
  border-color: #1677ff;
  background: #e6f4ff;
  color: #1677ff;
}

.handout-option-layout-btn .layout-icon {
  font-size: 11px;
  letter-spacing: -1px;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-question-option[data-handout-option-layout="2"] {
  display: inline-block !important;
  width: calc(50% - 8px) !important;
  margin: 2px 8px 0 0 !important;
  vertical-align: top;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-question-option[data-handout-option-layout="4"] {
  display: inline-block !important;
  width: calc(25% - 9px) !important;
  margin: 2px 6px 0 0 !important;
  vertical-align: top;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-question-option[data-handout-option-layout="1"] {
  display: block !important;
  width: 100% !important;
  margin: 2px 0 0 !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor] .handout-inline-question[data-handout-option-layout] {
  display: block !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor].hide-question-source [data-handout-question-source],
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor].hide-question-source .handout-question-source {
  display: none !important;
}

.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor].hide-question-difficulty [data-handout-question-difficulty],
.rich-editor.handout-mode ::v-deep .w-e-text-container [data-slate-editor].hide-question-difficulty .handout-question-difficulty {
  display: none !important;
}
</style>
